import { Link, useNavigate } from 'react-router-dom'
import { Palette, Check, Moon, Sun, LogOut, GraduationCap } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { useAuthStore } from '@/stores/auth-store'
import { useThemeStore } from '@/stores/theme-store'

const COLOR_THEME_OPTIONS = [
  { value: 'default' as const, label: 'Teal', color: '#0d9488' },
  { value: 'purple' as const, label: 'Purple', color: '#4c44e4' },
  { value: 'slate' as const, label: 'Slate', color: '#36404f' },
  { value: 'green' as const, label: 'Green', color: '#059467' },
  { value: 'neutral' as const, label: 'Neutral', color: '#737373' },
  { value: 'vercel' as const, label: 'Vercel', color: '#000000' },
  { value: 'blue' as const, label: 'Blue', color: '#4f46e5' },
] as const

function getUserInitials(displayName: string): string {
  return displayName
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

interface HeaderControlsProps {
  onLogout?: () => void
}

export function HeaderControls({ onLogout }: HeaderControlsProps) {
  const { user, logout } = useAuthStore()
  const { theme, toggleTheme, colorTheme, setColorTheme } = useThemeStore()
  const navigate = useNavigate()

  const handleLogout = () => {
    if (onLogout) {
      onLogout()
    } else {
      logout()
      navigate('/login')
    }
  }

  return (
    <div className="flex items-center gap-2">
      <Popover>
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            aria-label="Change color theme"
          >
            <Palette className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-3" align="end">
          <p className="mb-2 text-sm font-medium">Theme</p>
          <div className="flex gap-2">
            {COLOR_THEME_OPTIONS.map((option) => (
              <button
                key={option.value}
                type="button"
                className="relative flex h-7 w-7 items-center justify-center rounded-full transition-transform hover:scale-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                style={{ backgroundColor: option.color }}
                onClick={() => setColorTheme(option.value)}
                aria-label={option.label}
                title={option.label}
              >
                {colorTheme === option.value && (
                  <Check className="h-3.5 w-3.5 text-white" />
                )}
              </button>
            ))}
          </div>
        </PopoverContent>
      </Popover>

      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        asChild
        aria-label="Documentation"
        title="Documentation"
      >
        <Link to="/docs/getting-started">
          <GraduationCap className="h-4 w-4" />
        </Link>
      </Button>

      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8"
        onClick={toggleTheme}
        aria-label={`Switch to ${theme === 'dark' ? 'light' : 'dark'} mode`}
      >
        {theme === 'dark' ? (
          <Sun className="h-4 w-4" />
        ) : (
          <Moon className="h-4 w-4" />
        )}
      </Button>

      {user && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="relative h-8 w-8 rounded-full">
              <Avatar className="h-8 w-8">
                {user.avatarUrl && (
                  <AvatarImage src={user.avatarUrl} alt={user.displayName} />
                )}
                <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                  {getUserInitials(user.displayName)}
                </AvatarFallback>
              </Avatar>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="end">
            <DropdownMenuLabel className="font-normal">
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none">
                  {user.displayName}
                </p>
                <p className="text-xs leading-none text-muted-foreground">
                  {user.email}
                </p>
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={handleLogout}>
              <LogOut className="mr-2 h-4 w-4" />
              <span>Log out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  )
}
